import {
  Coding,
  SearchableSelectProps,
  SearchResponse
} from '@healthnz-prm/fhir-engine-renderer/types'
import { InputFieldset } from '../inputFieldset'
import {
  AsyncCombobox,
  type AsyncComboboxOption,
  type AsyncComboboxPage
} from '@/components/ui/async-combobox'

function codingKey(coding: Coding): string {
  return `${coding.system ?? ''}|${coding.code ?? coding.display ?? ''}`
}

export function toOption(coding: Coding): AsyncComboboxOption<Coding> {
  return {
    label: coding.display ?? coding.code ?? '',
    value: codingKey(coding),
    data: coding
  }
}

export function adaptPage(response: SearchResponse): AsyncComboboxPage<Coding> {
  return {
    options: response.options.map(toOption),
    loadMore: response.nextPage
      ? () => response.nextPage!().then(adaptPage)
      : undefined
  }
}

interface SearchInputProps {
  ariaLabel?: string
  disabled?: boolean
  getOptions: (
    filter: string | undefined,
    pageSize?: number | undefined
  ) => Promise<SearchResponse>
  name: string
  onBlur?: () => void
  onChange: (newValue: Coding | null) => void
  onFocus?: () => void
  placeholder?: string
  required?: boolean
  value: Coding | null
}

export function SearchInput({
  ariaLabel,
  disabled,
  getOptions,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  required,
  value
}: SearchInputProps) {
  return (
    <AsyncCombobox
      loadOptions={query => getOptions(query || undefined).then(adaptPage)}
      value={value ? toOption(value) : null}
      onChange={option => onChange(option?.data ?? null)}
      disabled={disabled}
      id={name}
      aria-label={ariaLabel ?? placeholder}
      placeholder={placeholder}
      searchPlaceholder={placeholder}
      required={required}
      onBlur={onBlur}
      onFocus={onFocus}
    />
  )
}

export default function SearchableSelect(props: SearchableSelectProps) {
  const {
    disabled,
    inputId,
    label,
    onBlur,
    onChange,
    onFocus,
    getOptions,
    required,
    value
  } = props

  return (
    <InputFieldset {...props}>
      <SearchInput
        ariaLabel={label}
        disabled={disabled}
        getOptions={getOptions}
        name={inputId}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        required={required}
        value={value}
      />
    </InputFieldset>
  )
}
